import {
    collectEvent,
    UserAction,
    userActionTypeEnum,
} from '@thoughtspot/metrics';
import { trackException } from '@thoughtspot/observability';
import _ from 'lodash';
import React, { useCallback } from 'react';
import { useBachAcHistoryContext } from '/@contexts/bach-ac-history-context';
import { useAppContext } from '/@contexts/global-app-context';
import {
    AnswerKindEnum,
    recallAnswerKind,
} from '/@services/bach/spotql-answer-kind-store';
import {
    getSession,
    TEST_SESSION_ID as BASE_TEST_SESSION_ID,
    useStatefulLazyQuery,
    useStatefulMutation,
    useStatefulQuery,
} from '/@services/stateful/stateful-service-hooks';
import { BlinkErrorCode } from '/@utils/error-handling/blink-error-boundary';
import {
    ApolloError,
    DocumentNode,
    LazyQueryResult,
    MutationHookOptions,
    QueryHookOptions,
    QueryResult,
    QueryTuple,
    useApolloClient,
    useLazyQuery,
} from '../../utils/error-handling/apollo-hooks-wrappers/custom-apollo-hooks';
import { BachSessionIdInput } from '../generated/graphql-types';
import {
    BachMutationFunctionOptions,
    BachMutationHookOptions,
    BachMutationTuple,
    bachPinboardSessionKeyQuery,
    bachSessionKeyQuery,
    bachSessionWithAcSessionKeyQuery,
    getGqlOperationNames,
    getStatefulMutationParams,
} from './bach.util';
import { useBachMutationWithoutSession } from './bach-base';

const NO_SESSION_FOUND_ERROR_CODE = '10097';

const BACH_EVENTS = {
    APOLLO_ERROR: 'BACH_APOLLO_ERROR',
    NETWORK_ERROR: 'BACH_NETWORK_ERROR',
} as const;

/**
 * Reports to Sentry for all graphQL and network errors.
 * Fires SESSION_STORE_OBJECT_NOT_FOUND Mixpanel event only for error 10097.
 * Suppresses tracking errors silently.
 */
function trackBachError(
    error: ApolloError,
    operationName: string | undefined,
    gqlOperationName: string | undefined,
    isPinboardSession?: boolean,
): void {
    try {
        const baseContext = {
            context: 'bach',
            operationName,
            gqlOperationName,
            ...(isPinboardSession !== undefined && { isPinboardSession }),
        };

        const hasGraphQLErrors = error.graphQLErrors?.length > 0;
        if (hasGraphQLErrors) {
            trackException(
                error,
                {
                    ...baseContext,
                    graphQLErrorCodes: error.graphQLErrors.map(
                        e => e.extensions?.code,
                    ),
                },
                BACH_EVENTS.APOLLO_ERROR,
            );
        }

        if (error.networkError) {
            trackException(
                error.networkError,
                baseContext,
                BACH_EVENTS.NETWORK_ERROR,
            );
        }

        // Selectively fire Mixpanel only for the targeted no-session error
        const match = error.graphQLErrors?.find(
            err =>
                (err.extensions?.code as string) ===
                NO_SESSION_FOUND_ERROR_CODE,
        );
        if (match) {
            collectEvent(
                new UserAction(
                    userActionTypeEnum.SESSION_STORE_OBJECT_NOT_FOUND,
                ),
                {
                    mutationName: operationName,
                    gqlOperationName,
                    ...(isPinboardSession !== undefined && {
                        isPinboardSession,
                    }),
                    errorMessage: match.message,
                },
            );
        }
    } catch {
        // Silently suppress tracking error
    }
}

/*
 * Do I need to send AC session info in my request?
 *
 * Only if your request meets BOTH of these conditions:
 * 1. Your request is a mutation (ie. we will get a new bach genNo in the response)
 * 2. It is possible that your request can be made immeditely after the user has edited the sage
 *    query, WITHOUT needing to fire the search to update the answer state first
 *
 * Eg: the user could type a token in the sage bar and then add a column from the data panel, all
 * without firing the query to update the answer. Hence the request to add the column from the data
 * panel should include AC session info.
 *
 * Eg: the user cannot switch between table/chart view without first firing the query and updating
 * the answer. While editing the sage query, the dark overlay that appears on the page prevents the
 * user from toggling table/chart view without first firing the query. Hence the request to update
 * the table/chart mode does not need to include AC session info.
 */

type BachSessionId = {
    sessionId: string;
    genNo: number;
};

/**
 * Session ID used in Jest tests (unit + integration). You should probably not need the AC session
 * details unless you are working on a test related to the sage bar or formula editor.
 */
export const TEST_SESSION_ID = {
    ...BASE_TEST_SESSION_ID,
};
export const TEST_SESSION_ID_WITH_AC_SESSION = {
    ...BASE_TEST_SESSION_ID,
    acSession: {
        sessionId: 'TEST_AC_SESSION_ID',
        genNo: 0,
    },
};

function useGenNoWorkingSetVariables(
    includeAcSessionInfo: boolean,
    sessionId?: string,
    includeAcGenNoWorkingSet = false,
) {
    const { sessionService } = useAppContext();
    const bachAcHistoryContext = useBachAcHistoryContext();

    return () => {
        if (!sessionService.isAnswerUndoStackEnabled()) {
            return {};
        }

        // An acSession key here survives useStatefulMutation's deep merge and puts a stray AC session on the SpotQL wire.
        const includeAcSession =
            includeAcSessionInfo &&
            recallAnswerKind(sessionId) !== AnswerKindEnum.Spotql;

        // Sent without the AC genNo, so Sage keeps every AC genNo the user can still Undo/Redo/Reset
        // to, without catching up the answer to the AC session.
        const acGenNoWorkingSet = includeAcGenNoWorkingSet
            ? bachAcHistoryContext?.getAcGenNoWorkingSet()
            : [];
        const acSessionId = bachAcHistoryContext?.getAcSessionId();
        const includeAcGenNoWorkingSetInfo =
            !_.isEmpty(acGenNoWorkingSet) && !!acSessionId;

        return {
            session: {
                genNoWorkingSet: bachAcHistoryContext?.getBachGenNoWorkingSet(),
                ...(includeAcSession
                    ? {
                          acSession: {
                              genNoWorkingSet: bachAcHistoryContext?.getAcGenNoWorkingSet(),
                          },
                      }
                    : {}),
                ...(includeAcGenNoWorkingSetInfo
                    ? { acGenNoWorkingSet, acSessionId }
                    : {}),
            },
        };
    };
}

/**
 * Convenience wrapper over Mutations to be done within a Bach Session.
 * This hook will automatically put the current 'genNo' from the cache given
 * a sessionId.
 */
export function useBachMutation<TData, TVariables>(
    sessionId: string,
    mutation: DocumentNode,
    options: BachMutationHookOptions<TData, TVariables> = {},
    handledErrors?: BlinkErrorCode[],
    isPinboardSession = false,
    // You most likely DO NOT need to send AC session info in a bach request! This is only needed
    // for certain flows close to the sage bar and formula editor. See the comment at the top of
    // this file for more info on how to determine if you need this flag.
    sendAcSessionInfo = false,
): BachMutationTuple<
    TData,
    Omit<TVariables, 'session' | 'dataPaginationParams'>
> {
    const getGenNoWorkingSetVariables = useGenNoWorkingSetVariables(
        sendAcSessionInfo,
        sessionId,
        true,
    );

    const { mutationName, gqlOperationName } = getGqlOperationNames(mutation);

    const wrappedOnError = React.useCallback(
        (error: ApolloError) => {
            trackBachError(
                error,
                mutationName,
                gqlOperationName,
                isPinboardSession,
            );
            return options.onError?.(error);
        },
        [mutationName, gqlOperationName, sessionId, options.onError],
    );

    const {
        typeName,
        sessionKeyQuery,
        nestedSessionKeys,
    } = getStatefulMutationParams(isPinboardSession, sendAcSessionInfo);

    const [mutateWithSession, state] = useStatefulMutation(
        useBachMutationWithoutSession<TData, TVariables>(
            sessionId,
            mutation,
            {
                ...options,
                onError: wrappedOnError,
                ...(isPinboardSession && {
                    trackPinboardMutationProgress: true,
                }),
            },
            handledErrors,
        ),
        sessionId,
        typeName,
        sessionKeyQuery,
        nestedSessionKeys,
    );

    const mutateWithHistory = React.useCallback(
        (opts: BachMutationFunctionOptions<TData, TVariables>) => {
            const variables = _.merge(
                {},
                options?.variables,
                opts?.variables,
                isPinboardSession ? {} : getGenNoWorkingSetVariables(),
            );

            return mutateWithSession({
                ...opts,
                variables,
            });
        },
        [
            isPinboardSession,
            mutateWithSession,
            ...Object.values(options.variables ?? {}),
        ],
    );

    return [mutateWithHistory, state];
}

/**
 * Convenience wrapper over Queries to be done within a Bach Session.
 * This hook will automatically put the current 'genNo' from the cache given
 * a sessionId.
 */
export function useBachQuery<TData, TVariables>(
    sessionId: string,
    query: DocumentNode,
    options: QueryHookOptions<TData, Omit<TVariables, 'session'>> = {},
    deadline?: number,
    // Bach queries should NEVER need to send AC session info! Doing so can reduce app performance!
    // If you need to send AC session info, you should be modeling your request as a mutation.
    sendAcSessionInfo = false,
    shouldUseApolloQuery = false,
): QueryResult<TData, TVariables> {
    const getGenNoWorkingSetVariables = useGenNoWorkingSetVariables(
        false,
        sessionId,
    );

    const finalOptions = _.merge({}, options, {
        variables: getGenNoWorkingSetVariables(),
    });

    return useStatefulQuery<TData, TVariables>(
        sessionId,
        'BachSessionId',
        sendAcSessionInfo
            ? bachSessionWithAcSessionKeyQuery
            : bachSessionKeyQuery,
        query,
        finalOptions,
        sendAcSessionInfo ? ['acSession'] : [],
        deadline,
        shouldUseApolloQuery,
    );
}

/**
 * Convenience wrapper over Lazy Queries to be done within a Bach Session.
 * This hook will automatically put the current 'genNo' from the cache given
 * a sessionId.
 */
export function useBachLazyQuery<TData, TVariables>(
    sessionId: string,
    query: DocumentNode,
    options:
        | MutationHookOptions<TData, TVariables>
        | QueryHookOptions<TData, TVariables> = {},
    // You most likely DO NOT need to send AC session info in a bach request! This is only needed
    // for certain flows close to the sage bar and formula editor. See the comment at the top of
    // this file for more info on how to determine if you need this flag.
    sendAcSessionInfo = false,
): QueryTuple<TData, TVariables> {
    const getGenNoWorkingSetVariables = useGenNoWorkingSetVariables(
        sendAcSessionInfo,
        sessionId,
    );

    const { mutationName: queryName, gqlOperationName } = getGqlOperationNames(
        query,
    );

    const wrappedOnError = React.useCallback(
        (error: ApolloError) => {
            trackBachError(error, queryName, gqlOperationName);
            return options.onError?.(error);
        },
        [queryName, gqlOperationName, sessionId, options.onError],
    );

    const [lazyQueryWithSession, state] = useStatefulLazyQuery<
        TData,
        TVariables
    >(
        useLazyQuery<TData, TVariables>(query, {
            ...options,
            onError: wrappedOnError,
        }) as QueryTuple<TData, TVariables>,
        sessionId,
        'BachSessionId',
        sendAcSessionInfo
            ? bachSessionWithAcSessionKeyQuery
            : bachSessionKeyQuery,
        sendAcSessionInfo ? ['acSession'] : [],
    );
    const { refetch: refetchWithSession, ...restState } = state;

    const lazyQueryWithHistory = React.useCallback(
        (opts: BachMutationFunctionOptions<TData, TVariables>) => {
            const variables = _.merge(
                {},
                options?.variables,
                opts?.variables,
                getGenNoWorkingSetVariables(),
            );

            return lazyQueryWithSession({
                ...opts,
                variables,
            });
        },
        [lazyQueryWithSession, ...Object.values(options?.variables ?? {})],
    );

    const refetchWithHistory = React.useCallback(
        (vars: TVariables) => {
            const variables = _.merge(
                {},
                options?.variables,
                vars,
                getGenNoWorkingSetVariables(),
            );

            return refetchWithSession(variables);
        },
        [refetchWithSession, ...Object.values(options?.variables ?? {})],
    );

    return [
        lazyQueryWithHistory,
        { refetch: refetchWithHistory, ...restState } as LazyQueryResult<
            TData,
            TVariables
        >,
    ];
}

export function useGetBachSession(
    sessionId: string,
    includeAcSession = false,
): () => BachSessionId | BachSessionIdInput {
    const client = useApolloClient();
    const getBachSession = useCallback(() => {
        const session = getSession(
            client,
            sessionId,
            'BachSessionId',
            includeAcSession
                ? bachSessionWithAcSessionKeyQuery
                : bachSessionKeyQuery,
        );
        return session;
    }, [sessionId]);
    return getBachSession;
}

export function useGetBachPinboardSession(
    sessionId: string,
): () => BachSessionId {
    const client = useApolloClient();
    const getBachSession = useCallback(() => {
        const session = getSession(
            client,
            sessionId,
            'BachPinboardSession',
            bachPinboardSessionKeyQuery,
            ['contextBookSessions'],
        );
        return session;
    }, [sessionId]);
    return getBachSession;
}

/**
 * Convenience wrapper over Queries to be done within a Pinboard Bach Session.
 * This hook will automatically put the current 'genNo' from the cache given
 * a sessionId.
 */
export function useBachPinboardQuery<TData, TVariables>(
    sessionId: string,
    query: DocumentNode,
    options: QueryHookOptions<TData, TVariables> = {},
): QueryResult<TData, TVariables> {
    return useStatefulQuery<TData, TVariables>(
        sessionId,
        'BachPinboardSession',
        bachPinboardSessionKeyQuery,
        query,
        options,
        ['contextBookSessions'],
    );
}

/**
 * Convenience wrapper over Lazy Queries to be done within a Pinboard Bach Session.
 * This hook will automatically put the current 'genNo' from the cache given
 * a sessionId.
 */
export function useBachLazyPinboardQuery<TData, TVariables>(
    sessionId: string,
    query: DocumentNode,
    options: MutationHookOptions<TData, TVariables> = {},
): QueryTuple<TData, TVariables> {
    const [lazyQueryWithSession, state] = useStatefulLazyQuery<
        TData,
        TVariables
    >(
        useLazyQuery<TData, TVariables>(query, options) as QueryTuple<
            TData,
            TVariables
        >,
        sessionId,
        'BachPinboardSession',
        bachPinboardSessionKeyQuery,
        ['contextBookSessions'],
    );

    const lazyQueryWithHistory = React.useCallback(
        (opts: BachMutationFunctionOptions<TData, TVariables>) => {
            const variables = _.merge({}, options?.variables, opts?.variables);

            return lazyQueryWithSession({
                ...opts,
                variables,
            });
        },
        [lazyQueryWithSession, ...Object.values(options?.variables ?? {})],
    );

    return [lazyQueryWithHistory, state];
}
